/**
 * pdp.js — PDP interactivity: gallery, variant picker, add-to-cart
 *
 * Expects in pdp-hero.liquid:
 *   [data-pdp-gallery]           — gallery container
 *   [data-gallery-main]          — the large <img>
 *   [data-gallery-thumb]         — each thumbnail <button>
 *   [data-gallery-zoom]          — zoom / open-lightbox <button>
 *   <dialog id="pdp-lightbox-SECTIONID">
 *     [data-lightbox-img]        — the full-size <img> inside the dialog
 *     [data-lightbox-close]      — close <button>
 *   </dialog>
 *   [data-variant-select]        — <select name="id"> for variant options
 *   [data-pdp-form]              — the <form action="/cart/add"> element
 *   [data-cart-toast]            — aria-live region for add-to-cart feedback
 *
 * All DOM queries use optional-chaining so missing elements never throw.
 */

import { prefersReducedMotion } from './theme.js';

// ─── Bootstrap ───────────────────────────────────────────────────────────────

document.addEventListener('DOMContentLoaded', () => {
  initGalleries();
  initSliders();
  initVariantPickers();
  initAddToCart();
});

// ─── 0. Hero image slider (simple fade gallery with prev/next arrows) ──────────

/**
 * Initialise every [data-pdp-slider]: cross-fades .pdp-hero__slide images,
 * driven by [data-pdp-prev]/[data-pdp-next], arrow keys (when focused), and
 * touch swipe. No-ops on single-image products.
 */
function initSliders() {
  document.querySelectorAll('[data-pdp-slider]').forEach((slider) => {
    if (slider.dataset.sliderBound) return;
    const slides = Array.from(slider.querySelectorAll('.pdp-hero__slide'));
    if (slides.length < 2) return;
    slider.dataset.sliderBound = '1';

    let i = 0;
    const show = (n) => {
      i = (n + slides.length) % slides.length;
      slides.forEach((s, idx) => s.classList.toggle('is-active', idx === i));
    };

    slider.querySelector('[data-pdp-prev]')?.addEventListener('click', () => show(i - 1));
    slider.querySelector('[data-pdp-next]')?.addEventListener('click', () => show(i + 1));

    slider.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') show(i - 1);
      else if (e.key === 'ArrowRight') show(i + 1);
    });

    // Touch swipe (mobile)
    let x0 = null;
    slider.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; }, { passive: true });
    slider.addEventListener('touchend', (e) => {
      if (x0 === null) return;
      const dx = e.changedTouches[0].clientX - x0;
      if (Math.abs(dx) > 40) show(dx < 0 ? i + 1 : i - 1);
      x0 = null;
    }, { passive: true });
  });
}

// ─── 1. Gallery ──────────────────────────────────────────────────────────────

/**
 * Initialise every [data-pdp-gallery] on the page.
 * Supports thumbnail swap, keyboard navigation, and a <dialog> lightbox.
 */
function initGalleries() {
  document.querySelectorAll('[data-pdp-gallery]').forEach(initSingleGallery);
}

/**
 * @param {HTMLElement} gallery
 */
function initSingleGallery(gallery) {
  const mainImg   = gallery.querySelector('[data-gallery-main]');
  const thumbBtns = /** @type {NodeListOf<HTMLButtonElement>} */ (
    gallery.querySelectorAll('[data-gallery-thumb]')
  );
  const zoomBtn   = gallery.querySelector('[data-gallery-zoom]');

  // Resolve the section id so we can find the matching lightbox dialog.
  const sectionEl = gallery.closest('[data-section-id]');
  const sectionId = sectionEl?.dataset?.sectionId ?? '';
  const dialog    = /** @type {HTMLDialogElement|null} */ (
    document.getElementById('pdp-lightbox-' + sectionId)
  );
  const lightboxImg   = dialog?.querySelector('[data-lightbox-img]');
  const lightboxClose = dialog?.querySelector('[data-lightbox-close]');

  if (!mainImg || thumbBtns.length === 0) return;

  // ── Thumb click ──────────────────────────────────────────────────────────

  thumbBtns.forEach((btn) => {
    btn.addEventListener('click', () => {
      swapMainImage(mainImg, thumbBtns, btn);
    });
  });

  // ── Keyboard navigation on the thumb strip ───────────────────────────────

  thumbBtns.forEach((btn, idx) => {
    btn.addEventListener('keydown', (e) => {
      let targetIdx = null;

      if (e.key === 'ArrowRight') {
        targetIdx = (idx + 1) % thumbBtns.length;
      } else if (e.key === 'ArrowLeft') {
        targetIdx = (idx - 1 + thumbBtns.length) % thumbBtns.length;
      }

      if (targetIdx !== null) {
        e.preventDefault();
        const targetBtn = thumbBtns[targetIdx];
        targetBtn.focus();
        swapMainImage(mainImg, thumbBtns, targetBtn);
      }
    });
  });

  // ── Lightbox ─────────────────────────────────────────────────────────────

  if (zoomBtn && dialog && lightboxImg) {
    zoomBtn.addEventListener('click', () => {
      lightboxImg.src = mainImg.src;
      lightboxImg.alt = mainImg.alt;
      dialog.showModal();
    });

    // Close via dedicated button
    lightboxClose?.addEventListener('click', () => dialog.close());

    // Close via backdrop click (click lands directly on <dialog>, not its child)
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog) dialog.close();
    });

    // Native <dialog> handles Esc automatically — no extra listener needed.
  }
}

/**
 * Swap the main gallery image and update active state on thumbnails.
 * Skips CSS transitions when the user prefers reduced motion.
 *
 * @param {HTMLImageElement} mainImg
 * @param {NodeListOf<HTMLButtonElement>} thumbBtns
 * @param {HTMLButtonElement} activeBtn
 */
function swapMainImage(mainImg, thumbBtns, activeBtn) {
  const newSrc = activeBtn.dataset.src;
  const newAlt = activeBtn.dataset.alt ?? '';

  if (!newSrc) return;

  if (!prefersReducedMotion) {
    mainImg.style.opacity = '0';
    mainImg.style.transition = 'opacity 0.15s ease';
  }

  mainImg.src = newSrc;
  mainImg.alt = newAlt;

  if (!prefersReducedMotion) {
    // After the browser paints the new src, fade back in.
    requestAnimationFrame(() => {
      mainImg.style.opacity = '1';
    });
  }

  // Update active / aria-selected state on all thumbs.
  thumbBtns.forEach((btn) => {
    const isActive = btn === activeBtn;
    btn.classList.toggle('is-active', isActive);
    btn.setAttribute('aria-selected', String(isActive));
  });
}

// ─── 2. Variant picker ───────────────────────────────────────────────────────

/**
 * Initialise every [data-variant-select] on the page.
 * The <select name="id"> drives the native/AJAX cart submission directly, so
 * here we only keep the URL's ?variant= param in sync on change. Shopify's
 * Liquid `money` filter (rendered at page load) stays the single source of
 * truth for the displayed price — no client-side currency formatting.
 */
function initVariantPickers() {
  document.querySelectorAll('[data-variant-select]').forEach(initSingleVariantPicker);
}

/**
 * @param {HTMLSelectElement} select
 */
function initSingleVariantPicker(select) {
  select.addEventListener('change', () => {
    const url = new URL(window.location.href);
    url.searchParams.set('variant', select.value);
    history.replaceState({}, '', url.toString());
  });
}

// ─── 3. Add to cart (AJAX) ──────────────────────────────────────────────────

/**
 * Progressive enhancement: intercept the native /cart/add form POST and submit
 * via fetch to /cart/add.js with the cart-drawer section so we can repaint it in
 * the same round-trip. On success the drawer slides open and the button flashes
 * a checkmark; on failure the real server error surfaces in an in-page toast.
 * If JS fails, the native <form action="/cart/add"> still works.
 */
function initAddToCart() {
  document.querySelectorAll('[data-pdp-form]').forEach(initSingleForm);
}

/**
 * @param {HTMLFormElement} form
 */
function initSingleForm(form) {
  form.addEventListener('submit', async (e) => {
    e.preventDefault();

    const btn = /** @type {HTMLButtonElement|null} */ (form.querySelector('[name="add"]'));
    // The button wraps a <span> label next to an SVG icon — update only the
    // span so the icon survives.
    const label = btn?.querySelector('span') ?? btn;
    const originalText = label?.textContent ?? '';

    if (btn) {
      btn.disabled = true;
      if (label) label.textContent = btn.dataset.loadingLabel ?? 'Adding…';
    }

    const formData = new FormData(form);
    // Ask Shopify to render the cart-drawer section alongside the add so we can
    // repaint the drawer (and header count) without a second request.
    formData.append('sections', 'cart-drawer');

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });
      const data = await res.json();

      // Shopify returns { status, message, description } on failure.
      if (!res.ok) {
        throw new Error(data.description || data.message || '');
      }

      // Repaint + open the drawer (cart.js owns the drawer; degrade to a toast
      // if it isn't present, e.g. drawer markup missing).
      const sectionHTML = data.sections?.['cart-drawer'];
      if (window.GrittCart) {
        if (sectionHTML) window.GrittCart.renderFromSection(sectionHTML);
        else await window.GrittCart.refresh();
        window.GrittCart.open();
      } else {
        showToast('success');
      }

      flashSuccess(btn, label, originalText);
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : '');
      if (btn) {
        btn.disabled = false;
        if (label) label.textContent = originalText;
      }
    }
  });
}

/**
 * Briefly show an "Added ✓" success state on the Add-to-Cart button, then
 * restore it. Skips the timed restore animation under reduced motion.
 *
 * @param {HTMLButtonElement|null} btn
 * @param {Element|null} label
 * @param {string} originalText
 */
function flashSuccess(btn, label, originalText) {
  if (!btn) return;
  const addedText = btn.dataset.addedLabel || 'Added';

  btn.classList.add('pdp-hero__add--success');
  if (label) label.textContent = addedText;

  setTimeout(() => {
    btn.classList.remove('pdp-hero__add--success');
    btn.disabled = false;
    if (label) label.textContent = originalText;
  }, 1600);
}

/** @type {ReturnType<typeof setTimeout>|undefined} */
let toastTimer;

/**
 * Render a message into the [data-cart-toast] aria-live region.
 * Localized defaults + cart URL are read from data-* attributes set by Liquid.
 *
 * @param {'success'|'error'} type
 * @param {string} [message]  Custom message; falls back to the localized default.
 */
function showToast(type, message) {
  const toast = document.querySelector('[data-cart-toast]');
  if (!toast) return;

  const text = message
    || (type === 'success'
      ? (toast.dataset.msgAdded || 'Added to cart')
      : (toast.dataset.msgError || 'Could not add to cart'));

  // Check icon for success, alert triangle for error — explicit w/h per house rule.
  const icon = type === 'success'
    ? '<svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true"><circle cx="9" cy="9" r="8" stroke="currentColor" stroke-width="1.5"/><path d="M5.5 9.2l2.3 2.3L12.7 6" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>'
    : '<svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true"><path d="M9 1.8l7.5 13H1.5L9 1.8z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/><path d="M9 7v3.2" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/><circle cx="9" cy="12.6" r="0.9" fill="currentColor"/></svg>';

  toast.classList.toggle('cart-toast--success', type === 'success');
  toast.classList.toggle('cart-toast--error', type === 'error');

  // Static icon + a View-cart link on success; message set via textContent (no XSS).
  toast.innerHTML = icon
    + '<span class="cart-toast__text"></span>'
    + (type === 'success'
      ? '<a class="cart-toast__link" href="' + (toast.dataset.cartUrl || '/cart') + '"></a>'
      : '');
  toast.querySelector('.cart-toast__text').textContent = text;
  const link = toast.querySelector('.cart-toast__link');
  if (link) link.textContent = toast.dataset.labelView || 'View cart';

  toast.hidden = false;
  requestAnimationFrame(() => toast.classList.add('is-visible'));

  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => toast.classList.remove('is-visible'), 5000);
}
